import React, { useState } from "react";
import { Clock, ExternalLink } from "lucide-react";

import {
	FloatingPanel,
	FloatingPanelBody,
	FloatingPanelContent,
	FloatingPanelDescription,
	FloatingPanelFooter,
	FloatingPanelHeader,
	FloatingPanelTitle,
} from "./FloatingPanel";

/**
 * Example usage of the `FloatingPanel` — a non-modal, floating companion panel.
 *
 * The scenario mirrors the real motivation: while working in the shift planner
 * you want to peek at the related time reports without leaving the page. The
 * mock grid behind the panel stays fully interactive (click the cells while the
 * panel is open) — that's the whole point of a non-modal floating panel vs a
 * modal Sheet.
 */
const FloatingPanelExample: React.FC = () => {
	const [open, setOpen] = useState(false);
	const [detailOpen, setDetailOpen] = useState(false);
	const [side, setSide] = useState<"right" | "left">("right");
	const [modal, setModal] = useState(false);
	const [clicks, setClicks] = useState(0);

	// The reports panel is 30rem wide; stack the detail panel just past it
	// (its width + a 1rem gap) so the two sit side by side.
	const REPORTS_WIDTH_REM = 30;
	const detailOffset = REPORTS_WIDTH_REM + 1;

	const triggerClasses =
		"inline-flex h-9 items-center justify-center rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground hover:bg-brand/90";
	const toggleClasses = (active: boolean) =>
		`inline-flex h-9 items-center justify-center rounded-md border px-4 py-2 text-sm font-medium ${
			active
				? "border-brand bg-brand/10 text-brand"
				: "border-input bg-background text-foreground hover:bg-accent"
		}`;

	const days = ["Mån 24", "Tis 25", "Ons 26", "Tor 27", "Fre 28"];
	const reports = [
		{ day: "Mån 24 aug", time: "09:30 – 15:00", hours: "5,0 h", status: "Attesterad" },
		{ day: "Tis 25 aug", time: "09:30 – 15:00", hours: "5,0 h", status: "Attesterad" },
		{ day: "Ons 26 aug", time: "09:30 – 15:00", hours: "5,0 h", status: "Rapporterad" },
		{ day: "Tor 27 aug", time: "09:30 – 15:00", hours: "5,0 h", status: "Rapporterad" },
		{ day: "Fre 28 aug", time: "09:30 – 15:00", hours: "5,0 h", status: "Ej rapporterad" },
	];

	return (
		<div className="space-y-6 p-6">
			<h2 className="text-xl font-bold">FloatingPanel</h2>
			<p className="max-w-2xl text-sm text-gray-600">
				A floating, inline companion panel — surfaces related content beside what you're doing
				without navigating away. Unlike the modal <code>Sheet</code>, it's non-modal by default: no
				backdrop, and the page behind it stays interactive.
			</p>

			{/* Controls */}
			<div className="flex flex-wrap items-center gap-4">
				<div className="flex items-center gap-2">
					<span className="text-sm font-medium text-gray-700">Side</span>
					{(["right", "left"] as const).map((s) => (
						<button key={s} className={toggleClasses(side === s)} onClick={() => setSide(s)}>
							{s}
						</button>
					))}
				</div>
				<button className={toggleClasses(modal)} onClick={() => setModal((m) => !m)}>
					{modal ? "Modal (with backdrop)" : "Non-modal (default)"}
				</button>
			</div>

			{/* Mock shift-planner grid to demonstrate the panel floating over an
			    interactive page. */}
			<div className="rounded-lg border border-gray-200 bg-white p-4">
				<div className="mb-3 flex items-center justify-between">
					<h3 className="font-semibold text-gray-800">Arbetspass</h3>
					<button className={triggerClasses} onClick={() => setOpen(true)}>
						Visa tidrapporter
					</button>
				</div>
				<div className="grid grid-cols-5 gap-2">
					{days.map((d) => (
						<button
							key={d}
							onClick={() => setClicks((c) => c + 1)}
							className="rounded-md border border-gray-200 bg-gray-50 px-2 py-3 text-left text-xs hover:border-brand hover:bg-brand/5"
						>
							<div className="font-medium text-gray-800">{d}</div>
							<div className="mt-1 rounded bg-green-100 px-1.5 py-1 text-green-800">
								09:30 – 15:00
							</div>
						</button>
					))}
				</div>
				<p className="mt-3 text-xs text-gray-500">
					Clicked cells while the panel is open: <span className="font-semibold">{clicks}</span> —
					proves the grid stays interactive.
				</p>
			</div>

			<FloatingPanel open={open} onOpenChange={setOpen} modal={modal}>
				<FloatingPanelContent side={side} overlay={modal} className="w-[30rem]">
					<FloatingPanelHeader>
						<div>
							<FloatingPanelTitle>Tidrapporter</FloatingPanelTitle>
							<FloatingPanelDescription>Uppdrag 39180 · Karima Anonymous</FloatingPanelDescription>
						</div>
						<button
							className={`${toggleClasses(detailOpen)} h-8`}
							onClick={() => setDetailOpen((d) => !d)}
						>
							Detaljer
						</button>
					</FloatingPanelHeader>

					<FloatingPanelBody className="space-y-2">
						{reports.map((r) => (
							<div
								key={r.day}
								className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2.5"
							>
								<div>
									<div className="text-sm font-medium text-gray-800">{r.day}</div>
									<div className="flex items-center gap-1 text-xs text-gray-500">
										<Clock className="h-3 w-3" />
										{r.time} · {r.hours}
									</div>
								</div>
								<span
									className={`rounded-full px-2 py-0.5 text-xs font-medium ${
										r.status === "Attesterad"
											? "bg-green-100 text-green-800"
											: r.status === "Rapporterad"
											? "bg-blue-100 text-blue-800"
											: "bg-gray-100 text-gray-600"
									}`}
								>
									{r.status}
								</span>
							</div>
						))}
					</FloatingPanelBody>

					<FloatingPanelFooter className="sm:justify-between">
						<a
							href="#"
							onClick={(e) => e.preventDefault()}
							className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
						>
							Öppna i tidrapporter
							<ExternalLink className="h-3.5 w-3.5" />
						</a>
						<button
							className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
							onClick={() => setOpen(false)}
						>
							Stäng
						</button>
					</FloatingPanelFooter>
				</FloatingPanelContent>
			</FloatingPanel>

			{/* A second panel stacked beside the first via `offset`, opened from
			    inside it — demonstrates multiple non-modal panels side by side. */}
			<FloatingPanel open={detailOpen} onOpenChange={setDetailOpen} modal={false}>
				<FloatingPanelContent side={side} offset={detailOffset} className="w-[22rem]">
					<FloatingPanelHeader>
						<FloatingPanelTitle>Detaljer</FloatingPanelTitle>
					</FloatingPanelHeader>
					<FloatingPanelBody className="space-y-3 text-sm text-gray-700">
						<p>
							This panel is stacked next to the reports panel using{" "}
							<code>offset={`{${detailOffset}}`}</code> (the reports panel's width + a 1rem gap).
						</p>
						<p>
							Both are open at once and both stay non-modal, so the grid behind them is still
							clickable.
						</p>
					</FloatingPanelBody>
				</FloatingPanelContent>
			</FloatingPanel>
		</div>
	);
};

export default FloatingPanelExample;
